import { useQuery } from '@tanstack/react-query'
import { LogOut, Play, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { NavLink } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import { getApiVersion } from '@/api/version'
import { LocaleSwitcher } from '@/components/LocaleSwitcher'
import { useNotifyDisplay } from '@/components/Notify'
import { getVisibleNavItems } from './nav-items'
import { useHeaderActionValue } from './HeaderAction'
import './TopBar.css'

export function TopBar() {
  const { t } = useTranslation()
  const { logout, isAdmin } = useAuth()
  const navItems = getVisibleNavItems()
  const action = useHeaderActionValue()
  const notice = useNotifyDisplay()
  const versionQuery = useQuery({
    queryKey: ['api-version'],
    queryFn: getApiVersion,
    staleTime: Infinity,
  })
  return (
    <header className="topbar">
      <div className="topbar__brand">
        <div className="topbar__mark">
          <Play fill="currentColor" strokeWidth={0} aria-hidden />
        </div>
        <div className="topbar__brand-flip">
          <div className="topbar__brand-flip-inner">
            <span className="topbar__brand-face topbar__brand-face--front">
              <span className="topbar__brand-name">podpipe</span>
            </span>
            <span className="topbar__brand-face topbar__brand-face--back">
              <span className="topbar__brand-versions">
                <span className="topbar__brand-version">ui v{__APP_VERSION__}</span>
                {versionQuery.isPending ? (
                  <span className="topbar__brand-version topbar__brand-version--loading">
                    <span className="topbar__version-skeleton" />
                  </span>
                ) : versionQuery.data ? (
                  <span className="topbar__brand-version">api v{versionQuery.data}</span>
                ) : null}
              </span>
            </span>
          </div>
        </div>
      </div>
      <nav className="topbar__nav">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            aria-label={t(item.label)}
            className={({ isActive }) => `topbar__nav-link${isActive ? ' is-active' : ''}`}
          >
            <span className="topbar__nav-icon" aria-hidden>
              {item.icon}
            </span>
            <span className="topbar__nav-text">
              [{item.shortcut}] {t(item.label)}
            </span>
          </NavLink>
        ))}
      </nav>
      <div className="topbar__right">
        {action ? <div className="topbar__action">{action}</div> : null}
        {isAdmin && (
          <NavLink
            to="/admin/users"
            className={({ isActive }) => `topbar__admin-btn${isActive ? ' is-active' : ''}`}
            title={t(($) => $.nav.admin)}
            aria-label={t(($) => $.nav.admin)}
          >
            <ShieldCheck size={14} aria-hidden />
          </NavLink>
        )}
        <LocaleSwitcher />
        <button
          className="topbar__user"
          onClick={() => {
            void logout()
          }}
          title={t(($) => $.nav.logout)}
          aria-label={t(($) => $.nav.logout)}
        >
          <LogOut size={14} aria-hidden />
        </button>
      </div>
      {notice ? (
        // key перезапускает flip-анимацию для каждого нового уведомления;
        // содержимое хедера перекрывается «перекидным табло».
        <div
          key={notice.id}
          className={`topbar__notify topbar__notify--${notice.severity}`}
          role="status"
          aria-live={notice.severity === 'error' ? 'assertive' : 'polite'}
        >
          {notice.message}
        </div>
      ) : null}
    </header>
  )
}
